import type { VariantClassesProps } from '@shared-ui/utils'

/**
 * Enum for date format types.
 */
export enum DateFormats {
    DEFAULT = 'DEFAULT',
    WITH_DOT = 'WITH_DOT',
    DATE_TIME = 'DATE_TIME',
}

/**
 * Interface for date format items.
 */
export interface DateFormatItems {
    [DateFormats.DEFAULT]: string
    [DateFormats.WITH_DOT]: string
    [DateFormats.DATE_TIME]: string
}

/**
 * Formats a date string according to the specified format.
 *
 * @param date - The date string to format.
 * @param locales - The locale(s) to use for formatting. Defaults to 'en-US'.
 * @param dateFormat - The format to use. Defaults to DateFormats.DEFAULT.
 * @returns The formatted date string.
 */
export const formatDate = (
    date: string | Date,
    locales: string | string[] = 'en-US',
    dateFormat: DateFormats = DateFormats.DEFAULT
): string => {
    if (!date) {
        return ''
    }
    const dateStr = typeof date === 'string' ? date : date.toISOString()
    const mapDateFormats: VariantClassesProps = {
        [DateFormats.DEFAULT]: defaultDateFormat(locales, dateStr),
        [DateFormats.WITH_DOT]: formatDateWithDot(dateStr),
        [DateFormats.DATE_TIME]: defaultDateTimeFormat(locales, dateStr),
    }

    return mapDateFormats[dateFormat]
}

/**
 * Formats a time string from 'HH:MM:SS' format to 'HH:MM' format.
 *
 * @param time - The time string to format (e.g., "12:00:00").
 * @returns The formatted time string in 'HH:MM' format.
 */
export const formatTime = (time: string): string => {
    // Handle time strings like "12:00:00"
    const [hours, minutes] = time.split(':')
    return `${hours}:${minutes}`
}

/**
 * Formats a time string with more robust parsing.
 *
 * @param time - The time string to format.
 * @returns The formatted time string in 'HH:MM' format.
 */
export const formatTimeRobust = (time: string): string => {
    try {
        // Create a date object with today's date and the given time
        const today = new Date().toISOString().split('T')[0] // Get today's date in YYYY-MM-DD format
        const dateTime = new Date(`${today}T${time}`)

        if (isNaN(dateTime.getTime())) {
            throw new Error('Invalid time format')
        }

        const hours = dateTime.getHours().toString().padStart(2, '0')
        const minutes = dateTime.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
    } catch (error) {
        console.error('Error formatting time:', error)
        return time // Return original time if formatting fails
    }
}
/**
 * Formats a date string into 'DD.MM.YYYY.' format.
 *
 * @param date - The date string to format.
 * @returns The formatted date string in 'DD.MM.YYYY.' format.
 */
export const formatDateWithDot = (date: string): string => {
    const formattedDate = new Date(date)
    const day = formattedDate.getDate().toString().padStart(2, '0')
    const month = (formattedDate.getMonth() + 1).toString().padStart(2, '0')
    const year = formattedDate.getFullYear().toString()
    return `${day}.${month}.${year}.`
}

/**
 * Formats a date string into 'MM/DD/YYYY' format.
 *
 * @param locales - The locale(s) to use for formatting. Defaults to 'en-US'.
 * @param date - The date string to format.
 * @returns The formatted date string in 'MM/DD/YYYY' format.
 */
export const defaultDateFormat = (
    locales: string | string[] = 'en-US',
    date: string
): string => {
    return Intl.DateTimeFormat(locales, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }).format(new Date(date))
}

/**
 * Formats a date string into 'MM/DD/YYYY, HH:mm:ss' format.
 *
 * @param locales - The locale(s) to use for formatting. Defaults to 'en-US'.
 * @param date - The date string to format.
 * @returns The formatted date string in 'MM/DD/YYYY, HH:mm:ss' format.
 */
export const defaultDateTimeFormat = (
    locales: string | string[] = 'en-US',
    date: string
): string => {
    return Intl.DateTimeFormat(locales, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false, // 24-hour format
    }).format(new Date(date))
}

/**
 * Converts a Date object to an ISO date string in 'YYYY-MM-DDT00:00:00.000Z' format.
 *
 * @param date - The Date object to convert. If null, returns an empty string.
 * @returns The ISO date string.
 */
export const transformToISODateString = (date: Date | null): string => {
    if (!date) {
        return ''
    }
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}T00:00:00.000Z`
}

export const getYear = (date?: string | number | null): string => {
    if (!date) return ''
    const year = new Date(date).getFullYear()
    return isNaN(year) ? '' : year.toString()
}
export const getTime = (date: string) => {
    const newDate = new Date(date)
    const hours = newDate.getUTCHours().toString().padStart(2, '0')
    const minutes = newDate.getUTCMinutes().toString().padStart(2, '0')
    const seconds = newDate.getUTCSeconds().toString().padStart(2, '0')

    // Format the time string
    return `${hours}:${minutes}:${seconds}`
}

/**
 * Converts a date string to an ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
 * but without altering the local time.
 *
 * @param date - The date string to transform (e.g., 'Tue Oct 01 2024 00:00:00 GMT+0200').
 * @returns The ISO date string in 'YYYY-MM-DDTHH:mm:ss.sssZ' format but in local time.
 */
export const formatToLocalISODateWithoutTimezone = (
    dateStr: string
): string => {
    // Extract the day, month, and year using a regular expression
    const dateParts = dateStr.match(/(\d{2})\.\s*(\d{2})\.\s*(\d{4})\./)

    if (!dateParts || dateParts.length !== 4) {
        throw new Error('Invalid date format. Expected format is DD. MM. YYYY.')
    }

    // Destructure the parts into day, month, and year
    const [, day, month, year] = dateParts

    // Create a new Date object with the parsed values
    const date = new Date(Number(year), Number(month) - 1, Number(day))

    if (isNaN(date.getTime())) {
        throw new Error('Invalid date value.')
    }

    // Convert the date to ISO format (with timezone correction if needed)
    return date.toISOString() // This returns the date in UTC with the 'Z'
}

/**
 * Gets the day of the week from a date string with capitalized first letter.
 *
 * @param date - The date string to extract day from.
 * @param locale - The locale to use for formatting. Defaults to 'en-US'.
 * @returns The day of the week with capitalized first letter (e.g., "Monday", "Tuesday").
 */
export const getDayOfWeek = (
    date: string | Date,
    locale: string = 'en-US'
): string => {
    const dateObj = new Date(date)
    const day = dateObj.toLocaleDateString(locale, { weekday: 'long' })
    // Capitalize the first letter
    return day.charAt(0).toUpperCase() + day.slice(1)
}

/**
 * Formats a date string with the day of the week.
 *
 * @param date - The date string to format.
 * @param locale - The locale to use for formatting. Defaults to 'en-US'.
 * @returns The formatted string with day of week and date.
 */
export const formatDateWithDay = (
    date: string,
    locale: string = 'en-US'
): string => {
    if (!date) {
        return ''
    }

    const day = getDayOfWeek(date, locale)
    const formattedDate = formatDate(date, locale)
    return `${day}, ${formattedDate}`
}
